import {
  Box,
  Col,
  DSIconButton,
  DSIconDeck,
  DSIconGhost,
  DSIconHandSingle,
  DSIconHandSingleDashed,
  Row,
  Text,
} from "@cdx/ds";
import {MouseEvent, forwardRef} from "react";
import {TooltipForChild, getMetaKeyLabel, isWithMetaKey, useGlobalKeyPress} from "@cdx/common";
import {moveCardsToDeck} from "../../lib/cross-project-cards-or-decks";
import useMutation from "../../lib/hooks/useMutation";
import {
  canAddHandToQueueErrors,
  canDiscardHandFromQueueErrors,
  getAllCardPos,
  isCardInUsersHandQueue,
} from "../hand-queue/hand-queue-utils";
import {cardHighlightActions} from "../card-highlight/useCardHighlight";
import {useHandQueueOverlay} from "./HandQueueOverlay";
import {MiniDeckButton, getCardProjectIfRestricted} from "./card-container-utils";
import routes from "../../routes";
import publicProjectRoutes from "../public-project/public-project-routes";
import {DSDeckPickerButton} from "./DSDeckPicker";
import {hasPermissionToGuardCard} from "../../lib/permissions";
import {CardPropOverlayForExistingCard} from "../../components/RichTextarea/Lexical/CardPropChangeForChild";
import {Card, CardId} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {UserId} from "../../cdx-models/User";
import DSAvatar from "../../components/DSAvatar/DSAvatar";
import {useInstance} from "../../lib/mate/mate-utils";
import {Deck} from "../../cdx-models/Deck";

export const PublicTopArea = ({card, arenaCtx}: {card: any; arenaCtx: any}) => {
  if (card.deck && card.deck === arenaCtx.model) return null;
  if (!card.deck) {
    return (
      <Row align="center" height="28px">
        <Box size={12} color="secondary">
          <DSIconGhost size={20} inline /> no Deck
        </Box>
      </Row>
    );
  } else {
    if (!card.deck.$meta.isLoaded && arenaCtx.type === "deck") return null;
    return (
      <Row sp="4px" align="center" height="28px">
        <Box size={12} color="secondary">
          in
        </Box>
        <MiniDeckButton
          deck={card.deck}
          maxChars={50}
          buttonProps={{
            to: publicProjectRoutes.publicProjectWithDeck.getUrl(card.deck.project, card.deck),
          }}
        />
      </Row>
    );
  }
};

type DeckPickerProps = {
  card: Card;
  currentContextDeck: Deck | null;
  canModify: boolean | null;
  root: Root;
  category: string;
};
const DeckPicker = (props: DeckPickerProps) => {
  const {card, currentContextDeck, canModify, root, category} = props;
  const {deck} = card;

  const badDeck = deck && currentContextDeck && deck.$meta.isLoaded && currentContextDeck !== deck;
  const showOpenButton = () => {
    if (!deck) return false;
    if (!currentContextDeck) return true;
    if (!deck.$meta.isLoaded) return false;
    return currentContextDeck !== deck;
  };

  const cardId = card.$meta.get("cardId", null)!;

  return (
    <Row sp="12px" align="center">
      <DSDeckPickerButton
        deck={deck}
        maxChars={32}
        badDeck={badDeck}
        canModify={canModify}
        root={root}
        onPickDeckId={(deckId) => moveCardsToDeck([cardId], deckId)}
        category={category}
        getProjects={() => getCardProjectIfRestricted({card, root})}
        renderOverlay={({overlayProps, close}) => (
          <CardPropOverlayForExistingCard
            cardId={cardId}
            overlayProps={overlayProps}
            onClose={close}
            initialTab="deck"
          />
        )}
      />
      {showOpenButton() && (
        <DSIconButton
          variant="tertiary"
          size="sm"
          negatePadding
          to={routes.deckWithCard.getUrl(deck!, card)}
          icon={<DSIconDeck />}
          label="Open"
        />
      )}
    </Row>
  );
};

const HandButton = forwardRef<HTMLElement, {onClick: (e: any) => unknown; notInHand: boolean}>(
  ({onClick, notInHand, ...rest}, ref) => {
    return (
      <DSIconButton
        ref={ref}
        onClick={onClick}
        icon={notInHand ? <DSIconHandSingleDashed size={16} /> : <DSIconHandSingle size={16} />}
        active={!notInHand}
        variant="tertiary"
        size="sm"
        {...rest}
      />
    );
  }
);

const UserLine = ({userId, pos}: {userId: UserId; pos: number}) => {
  const user = useInstance("user", userId);
  return (
    <Row sp="8px" align="center" key={userId}>
      <DSAvatar size={16} user={user} />
      <Text type="label12" color="primary">
        {user?.name}
      </Text>
      <Text type="label12light" color="primary" ml="auto">
        #{pos}
      </Text>
    </Row>
  );
};

const CurrentQueueInfo = ({cardId, root}: {cardId: CardId; root: Root}) => {
  const cardPosMap = getAllCardPos(cardId, root.account);
  if (!cardPosMap) return "Card is not in hand.";
  return (
    <Col sp="8px">
      <Text type="label12light" color="primary" as="h3">
        In the hand of
      </Text>
      <Col sp="4px">
        {[...cardPosMap].map(([userId, pos], idx) => (
          <UserLine userId={userId} pos={pos} key={idx} />
        ))}
      </Col>
    </Col>
  );
};

const InteractionItem = ({label, description}: {label: string; description: string}) => (
  <Row sp="16px" align="baseline">
    <Text type="label11Caps" color="secondary">
      {label}
    </Text>
    <Text type="prose12" size={12} ml="auto">
      {description}
    </Text>
  </Row>
);

const HandQueueHandler = ({card, root}: {card: Card; root: Root}) => {
  const meId = root.loggedInUser?.$meta.get("id", null);
  const assigneeId = card.assignee?.$meta.get("id", null) ?? null;
  const cardId = card.$meta.get("cardId", null);
  const isInQueue = isCardInUsersHandQueue({cardId, account: root.account, userId: meId});
  const {modal, setModalOpen} = useHandQueueOverlay({root, card});

  const [addCards] = useMutation("handQueue", "addCardsToOwner");
  const [removeCards] = useMutation("handQueue", "removeCards");
  const rawMetaError = isInQueue
    ? canDiscardHandFromQueueErrors(card)
    : canAddHandToQueueErrors(card, meId, root);

  const getOfferOwnerInfo = () => {
    if (!assigneeId) return null;
    if (assigneeId === meId) return null;
    const inQueue = isCardInUsersHandQueue({cardId, account: root.account, userId: assigneeId});
    if (inQueue) return {userId: assigneeId, type: "discard"} as const;
    const addErr = canAddHandToQueueErrors(card, assigneeId, root);
    if (!addErr) return {userId: assigneeId, type: "add"} as const;
    return null;
  };
  const offerOwnerInfo = getOfferOwnerInfo();

  const metaError =
    rawMetaError &&
    `${isInQueue ? "Can't discard this card" : "Can't add this card"}: ${rawMetaError}`;

  const toggleHandPresence = () => {
    if (metaError || !cardId) return;
    if (isInQueue) {
      return removeCards({cardIds: [cardId]});
    } else {
      if (!meId) return;
      return addCards({cardIds: [cardId], accountId: root.account.id}).then(() => {
        cardHighlightActions.highlightCardId(card.cardId, 2000);
      });
    }
  };

  const handleClick = (e: MouseEvent) => {
    if (isWithMetaKey(e)) {
      return toggleHandPresence();
    } else {
      setModalOpen((v) => !v);
    }
  };

  useGlobalKeyPress({
    key: "h",
    fn: () => setModalOpen((v) => !v),
    description: "Open hand overlay",
    category: "Card detail view",
  });

  useGlobalKeyPress({
    code: "KeyH",
    fn: toggleHandPresence,
    description: "Add to or discard from hand.",
    category: "Card detail view",
    withAlt: true,
  });

  const tooltip = () => {
    return (
      <Col pa="8px" sp="8px">
        <CurrentQueueInfo cardId={card.cardId as CardId} root={root} />
        {metaError && (
          <Text type="prose12" size={12}>
            {metaError}
          </Text>
        )}
        <Col pt="12px">
          <InteractionItem label="Click" description="Open Hand Overlay." />
          <InteractionItem
            label={`${getMetaKeyLabel()} + click`}
            description={`Directly ${isInQueue ? "discard from" : "add to"} your Hand.`}
          />
          {offerOwnerInfo && (
            <InteractionItem
              label={`${getMetaKeyLabel()} + shift + click`}
              description={`Directly ${offerOwnerInfo.type === "discard" ? "discard from" : "add to"} owner's Hand.`}
            />
          )}
        </Col>
      </Col>
    );
  };

  return (
    <>
      {modal}
      <Box ml="auto">
        <TooltipForChild delayed tooltip={tooltip}>
          <HandButton onClick={handleClick} notInHand={!isInQueue} />
        </TooltipForChild>
      </Box>
    </>
  );
};

type NonPublicTopAreaProps = {
  root: any;
  card: any;
  arenaCtx: any;
  canModify: boolean | null;
  category: string;
};
export const NonPublicTopArea = (props: NonPublicTopAreaProps) => {
  const {root, card, arenaCtx, canModify, category} = props;
  const isGuard = hasPermissionToGuardCard(root, card);
  return (
    <Row sp="4px" height="28px" align="center">
      <DeckPicker
        root={root}
        card={card}
        currentContextDeck={arenaCtx && arenaCtx.type === "deck" ? arenaCtx.model : null}
        canModify={canModify && isGuard}
        category={category}
      />
      {canModify && <HandQueueHandler card={card} root={root} />}
    </Row>
  );
};
